<template>
  <v-dialog v-model="dialogModel" max-width="800" persistent>
    <v-card class="pa-4">
      <v-card-title>Add New Task</v-card-title>

      <v-card-text>
        <div class="d-flex flex-column ga-5">
          <span class="error-message" v-for="e in errors" :key="e">{{
            e
          }}</span>

          <!-- Form Fields -->
          <template v-for="field in formFields" :key="field.id">
            <div
              :class="[
                field.type === 'autocomplete' ? 'filed-autocomplete' : 'field',
                'd-flex flex-column ga-2',
              ]"
            >
              <label
                v-if="field.type !== 'switch'"
                :ref="field.ref"
                :for="field.id"
              >
                {{ field.label }}
                <span v-if="field.required" class="required">*</span>
              </label>

              <!-- Regular Input -->
              <input
                v-if="field.type === 'input'"
                :id="field.id"
                v-model="formData[field.id]"
                :type="field.inputType || 'text'"
                @input="updateValidationClass(field.ref, formData[field.id])"
              />

              <!-- Date Input -->
              <input
                v-else-if="field.type === 'date'"
                :id="field.id"
                v-model="formData[field.id]"
                type="text"
                :ref="field.id"
                @input="updateValidationClass(field.ref, formData[field.id])"
              />

              <!-- Autocomplete -->
              <v-autocomplete
                v-else-if="field.type === 'autocomplete'"
                v-model="formData[field.id]"
                :items="field.items"
                :multiple="field.multiple"
                :item-value="field.itemValue"
                :item-title="field.itemTitle"
                hide-details
                chips
                @change="updateValidationClass(field.ref, formData[field.id])"
              />

              <!-- Switch -->
              <div
                v-else-if="field.type === 'switch'"
                class="d-flex justify-end align-center ga-2 custom-switch"
              >
                <label :for="field.id">{{ field.label }}</label>
                <v-switch
                  color="green"
                  v-model="formData[field.id]"
                  hide-details
                  inset
                  :true-value="1"
                  :false-value="0"
                ></v-switch>
              </div>
            </div>
          </template>
        </div>
      </v-card-text>

      <v-card-actions class="sticky-footer-dialog">
        <v-spacer></v-spacer>
        <span class="cancel-btn cursor-pointer" @click="cancelDialog"
          >Cancel</span
        >
        <span class="choose-btn cursor-pointer" @click="saveTask">Save</span>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.css";
import { getFieldError } from "@/utils/formValdiation.js";
import apiClient from "@/core/buildRequest";
import { useContactsStore } from "@/store/crm/useContactsStore";
import { useAccountsStore } from "@/store/crm/useAccountsStore";
import { useProjectsStore } from "@/store/crm/useProjectsStore";
import { useDealsStore } from "@/store/crm/useDealsStore";
import { useTasksStore } from "@/store/crm/useTasksStore";
import { useGeneralStore } from "@/store/General";
export default {
  name: "TaskDialog",

  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    contacts: {
      type: Array,
      default: () => [],
    },
    accounts: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      selectcontacts: "",
      selectaccounts: "",
      selectprojects: "",
      selectDeals: "",
      selectTasks: "",
      selectagents: "",
      formData: {
        name: "",
        description: "",
        start_date: "",
        due_date: "",
        add_start_date_to_calendar: 1,
        add_to_calendar: 1,
        assigness: [],
        contact_id: [],
        account_id: null,
        status: null,
        project_id: null,
        deal_id: null,
        cost: "",
        weight: "",
        estimated_bonus: "",
      },
      errors: [],
      fieldErrors: {},
      flatpickrInstances: {},
      contacts_store: useContactsStore(),
      accounts_store: useAccountsStore(),
      projects_store: useProjectsStore(),
      deals_store: useDealsStore(),
      tasks_store: useTasksStore(),
      genral_store: useGeneralStore(),
    };
  },

  computed: {
    dialogModel: {
      get() {
        return this.dialog;
      },
      set(value) {
        this.$emit("update:dialog", value);
      },
    },

    formFields() {
      return [
        {
          id: "name",
          label: "Task Name",
          type: "input",
          required: true,
          ref: "taskNameInput",
        },
        {
          id: "description",
          label: "Description",
          type: "input",
          required: true,
          ref: "descriptionInput",
        },
        {
          id: "start_date",
          label: "Start Date",
          type: "date",
          required: true,
          ref: "startDateInput",
        },
        // {
        //   id: "add_start_date_to_calendar",
        //   label: "Add Start Date To Calendar",
        //   type: "switch",
        // },
        {
          id: "due_date",
          label: "Due Date",
          type: "date",
          required: true,
          ref: "dueDateInput",
        },
        {
          id: "add_to_calendar",
          label: "Add Due Date To Calendar",
          type: "switch",
        },
        {
          id: "member_id",
          label: "Assignees",
          type: "autocomplete",
          items: this.selectagents,
          multiple: true,
          required: true,
          ref: "assigneesInput",
          itemTitle: "name",
          itemValue: "id",
        },
        {
          id: "contact_id",
          label: "Contacts",
          type: "autocomplete",
          items: this.selectcontacts,
          itemTitle: "contact_number",
          itemValue: "id",
          multiple: true,
          required: true,
          ref: "contactsInput",
        },
        {
          id: "account_id",
          label: "Accounts",
          type: "autocomplete",
          items: this.selectaccounts,
          itemTitle: "account_number",
          itemValue: "id",
          multiple: false,
          required: true,
          ref: "accountsInput",
        },
        {
          id: "status",
          label: "Status",
          type: "autocomplete",
          items: ["active", "completed", "pending", "on_hold", "closed"],
          required: true,
          ref: "statusInput",
        },
        {
          id: "project_id",
          label: "Project",
          type: "autocomplete",
          items: this.selectprojects,
          itemTitle: "project_number",
          itemValue: "id",
          required: true,
          ref: "projectInput",
        },
        {
          id: "deal_id",
          label: "Deal",
          type: "autocomplete",
          items: this.selectDeals,
          itemTitle: "deal_number",
          itemValue: "id",
          required: true,
          ref: "dealInput",
        },
        {
          id: "cost",
          label: "Cost",
          type: "input",
          inputType: "number",
          required: true,
          ref: "costInput",
        },
        {
          id: "weight",
          label: "Weight",
          type: "input",
          inputType: "number",
          required: true,
          ref: "weightInput",
        },
        {
          id: "estimated_bonus",
          label: "Estimated Bonus",
          type: "input",
          inputType: "number",
          required: true,
          ref: "estimatedBonusInput",
        },
      ];
    },
    all_contacts() {
      return this.contacts_store?.getContacts || [];
    },
    all_accounts() {
      return this.accounts_store?.getAccounts || [];
    },
    all_projects() {
      return this.projects_store?.getProjects || [];
    },
    all_deals() {
      return this.deals_store?.getDeals || [];
    },
  },

  watch: {
    dialog(newVal) {
      if (newVal) {
        this.getAllcontacts();
        this.getAllAccounts();
        this.getAllprojects();
        this.getAllDeals();
        this.getAllTasks();
        this.getAllagents();
        this.$nextTick(() => {
          this.initializeFlatPickr();
        });
      } else {
        this.destroyFlatPickr();
      }
    },
    "formData.add_to_calendar"(newVal) {
      if (newVal) {
        this.formData.add_to_calendar = 1;
      } else {
        this.formData.add_to_calendar = 0;
      }
    },
    "formData.add_start_date_to_calendar"(newVal) {
      if (newVal) {
        this.formData.add_start_date_to_calendar = 1;
      } else {
        this.formData.add_start_date_to_calendar = 0;
      }
    },
    contacts: {
      handler(newVal) {
        this.formData.contact_id = newVal;
      },
      deep: true,
    },

    accounts: {
      handler(newVal) {
        this.formData.accounts = newVal;
      },
      deep: true,
    },
  },

  beforeUnmount() {
    this.destroyFlatPickr();
  },

  methods: {
    initializeFlatPickr() {
      this.destroyFlatPickr();

      const startDateEl = this.$refs.start_date?.[0];
      if (startDateEl) {
        this.flatpickrInstances.startDate = flatpickr(startDateEl, {
          enableTime: false,
          dateFormat: "Y-m-d",
          onChange: (selectedDates) => {
            this.formData.start_date = selectedDates[0]
              ? selectedDates[0].toISOString().split("T")[0]
              : "";
            this.updateValidationClass(
              "startDateInput",
              this.formData.start_date
            );
          },
        });
      }

      const dueDateEl = this.$refs.due_date?.[0];
      if (dueDateEl) {
        this.flatpickrInstances.dueDate = flatpickr(dueDateEl, {
          enableTime: false,
          dateFormat: "Y-m-d",
          onChange: (selectedDates) => {
            this.formData.due_date = selectedDates[0]
              ? selectedDates[0].toISOString().split("T")[0]
              : "";
            this.updateValidationClass("dueDateInput", this.formData.due_date);
          },
        });
      }
    },

    destroyFlatPickr() {
      Object.values(this.flatpickrInstances).forEach((instance) => {
        if (instance) {
          instance.destroy();
        }
      });
      this.flatpickrInstances = {};
    },

    validateForm() {
      this.errors = [];
      this.fieldErrors = {};
      let isValid = true;

      for (const field of this.formFields) {
        if (!field.required) continue;

        const value = this.formData[field.id];
        const error = getFieldError(field.label, value);

        if (error) {
          isValid = false;
          this.fieldErrors[field.id] = error;
          const el = this.$refs[field.ref]?.[0];
          if (el) {
            el.classList.add("error-validation");
          }
        }
      }

      return isValid;
    },

    updateValidationClass(ref, value) {
      const el = this.$refs[ref]?.[0];
      if (el) {
        el.classList[!value ? "add" : "remove"]("error-validation");
      }
    },

    resetForm() {
      Object.keys(this.formData).forEach((key) => {
        this.formData[key] = Array.isArray(this.formData[key]) ? [] : "";
      });
      this.formData.add_start_date_to_calendar = true;
      this.formData.add_to_calendar = true;
      this.errors = [];
      this.fieldErrors = {};
    },

    cancelDialog() {
      this.resetForm();
      this.$emit("update:dialog", false);
    },

    async saveTask() {
      if (!this.validateForm()) return;
      this.genral_store.ShowLoading();
      try {
        await apiClient.post("/leads-managment/tasks", this.formData);
        this.resetForm();
        this.$emit("update:dialog", false);
        this.tasks_store.getAllTasks();
        this.genral_store.showSnackbar("Task saved successfully", "#237276");
      } catch (error) {
        this.errors.push(error.response?.data?.msg || "An error occurred");
        console.error(error);
      } finally {
        this.genral_store.HideLoading();
      }
    },
    //get Allagents
    async getAllagents() {
      try {
        var response = await apiClient.get(`/leads-managment/all-agents`);
        const agentid = response.data.data;
        this.selectagents = Array.isArray(agentid)
          ? agentid.map((e) => ({
              id: e.id,
              name: e.first_name + " " + e.last_name,
            }))
          : [];
      } catch (err) {
        console.error("Error fetching deals:", err);
      }
    },
    //get Allcontacts
    async getAllcontacts() {
      try {
        var response = await apiClient.get(`/leads-managment/list-contacts`);
        this.selectcontacts = response.data.data.data;
      } catch (err) {
        console.error("Error fetching deals:", err);
      }
    },
    // get Accounts
    async getAllAccounts() {
      try {
        var response = await apiClient.get(`/leads-managment/accounts`);
        this.selectaccounts = response.data.data.data;
      } catch (err) {
        console.error("Error fetching deals:", err);
      }
    },
    //get projects
    async getAllprojects() {
      try {
        var response = await apiClient.get(`/leads-managment/projects`);
        this.selectprojects = response.data.data.data;
      } catch (err) {
        console.error("Error fetching deals:", err);
      }
    },
    //get Deal
    async getAllDeals() {
      try {
        var response = await apiClient.get(`/leads-managment/deals`);
        this.selectDeals = response.data.data.data;
      } catch (err) {
        console.error("Error fetching deals:", err);
      }
    },
    //get tasks
    async getAllTasks() {
      try {
        var response = await apiClient.get(`/leads-managment/tasks`);
        this.selectTasks = response.data.data.data;
      } catch (err) {
        console.error("Error fetching deals:", err);
      }
    },
  },
  mounted() {
    if (this.dialog) {
      this.initializeFlatPickr();
    }
  },
};
</script>
